import { cilLink, cilPlus, cilTrash } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { CCol, CRow } from '@coreui/react';
import { useState } from 'react';
import { useShowSnackbar } from 'src/hooks/useShowSnackbar';
import globalutil from 'src/util/globalutil';
import CustomInput from '../InputsComponent/CustomInput';
import CustomSelectInput from '../InputsComponent/CustomSelectInput';
import Button from '../UI/Button';

const MAX_WIDTH = 6000;
const MAX_HEIGHT = 6000;

function DAAttachment({ attachmentRows, setAttachmentRows }) {
  const showSnackbar = useShowSnackbar();

  const [nextId, setNextId] = useState(10);

  // Get available options (exclude already selected ones except current row)
  const getAvailableOptions = (currentRowId) => {
    const selectedIds = attachmentRows
      .filter((row) => row.id !== currentRowId && row.docType)
      .map((row) => row.docType);

    return globalutil
      .document_types()
      .filter((option) => !selectedIds.includes(option.id.toString()));
  };

  const handleAddRow = () => {
    const newRow = {
      id: nextId, // use incremental id
      docType: '',
      docCat: '',
      frontAttachment: null,
      frontAttachmentPreview: '',
      backAttachment: null,
      backAttachmentPreview: '',
    };
    setAttachmentRows([...attachmentRows, newRow]);
    setNextId(nextId + 1); // increment for next use
  };

  const handleRemoveRow = (id) => {
    if (attachmentRows.length > 1) {
      setAttachmentRows(attachmentRows.filter((row) => row.id !== id));
    }
  };

  const handleSelectChange = (id, value) => {
    const findCategory = globalutil.document_types()?.find((dt) => dt?.id == value);
    setAttachmentRows(
      attachmentRows.map((row) =>
        row.id === id ? { ...row, docType: value, docCat: findCategory?.desc || '' } : row,
      ),
    );
  };

  const handleFileChange = (id, side, file) => {
    if (file) {
      const isPDF = file.type === 'application/pdf';
      const isDoc = file.type === 'application/msword'; // .doc
      const isDocx =
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'; // .docx

      const reader = new FileReader();

      reader.onloadend = () => {
        // Handle PDF
        if (isPDF) {
          setAttachmentRows(
            attachmentRows.map((row) =>
              row.id === id
                ? {
                    ...row,
                    [`${side}Attachment`]: file,
                    [`${side}AttachmentPreview`]: 'pdf',
                  }
                : row,
            ),
          );
          return;
        }

        // Handle Word Files (.doc / .docx)
        if (isDoc || isDocx) {
          setAttachmentRows(
            attachmentRows.map((row) =>
              row.id === id
                ? {
                    ...row,
                    [`${side}Attachment`]: file,
                    [`${side}AttachmentPreview`]: isDoc ? 'doc' : 'docx',
                  }
                : row,
            ),
          );
          return;
        }

        // Handle images (validation required)
        const img = new Image();
        img.src = reader.result;

        img.onload = () => {
          const { width, height } = img;
          if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            if (showSnackbar) {
              showSnackbar('Image size should be less than 6000 x 6000', 'warning');
            } else {
              alert('Image size should be less than 6000 x 6000');
            }
            return;
          }

          setAttachmentRows(
            attachmentRows.map((row) =>
              row.id === id
                ? {
                    ...row,
                    [`${side}Attachment`]: file,
                    [`${side}AttachmentPreview`]: reader.result,
                  }
                : row,
            ),
          );
        };

        img.onerror = () => {
          if (showSnackbar) {
            showSnackbar('Invalid image file', 'error');
          } else {
            alert('Invalid image file');
          }
        };
      };

      reader.onerror = () => {
        if (showSnackbar) {
          showSnackbar('Error reading file', 'error');
        } else {
          alert('Error reading file');
        }
      };

      reader.readAsDataURL(file);
    } else {
      // Clear file
      setAttachmentRows(
        attachmentRows.map((row) =>
          row.id === id
            ? {
                ...row,
                [`${side}Attachment`]: null,
                [`${side}AttachmentPreview`]: '',
              }
            : row,
        ),
      );
    }
  };

  return (
    <div className="mt-3">
      {attachmentRows.map((row, index) => {
        const availableOptions = getAvailableOptions(row.id);

        return (
          <CRow key={row.id} className="mb-3 align-items-end pb-3">
            <CCol md={3}>
              <CustomSelectInput
                options={availableOptions}
                id={`docType-${row.id}`}
                name="docType"
                label="Document Type"
                value={row.docType}
                onChange={(e) => handleSelectChange(row.id, e.target.value)}
                isRequired={false}
                message="Please select a document type"
                disableOption="Select Document Type"
                icon={cilLink}
                optionsList={(option) => `${option?.name || ''} (${option?.desc || ''})`}
              />
            </CCol>

            <CCol md={3}>
              <CustomInput
                type="file"
                id={`front-${row.id}`}
                name={`frontAttachment-${row.id}`}
                label=" Attachment 1"
                accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                helperText={row.frontAttachment ? row.frontAttachment.name : 'No file chosen'}
                onChange={(e) => handleFileChange(row.id, 'front', e.target.files[0])}
                src={
                  row.frontAttachment?.type === 'application/pdf'
                    ? 'pdf'
                    : row.frontAttachment?.type === 'application/msword'
                    ? 'doc'
                    : row.frontAttachment?.type ===
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ? 'docx'
                    : row.frontAttachmentPreview
                }
                isRequired={false}
                message="Front attachment is required"
              />
            </CCol>

            <CCol md={3}>
              <CustomInput
                type="file"
                id={`back-${row.id}`}
                name={`backAttachment-${row.id}`}
                label=" Attachment 2"
                accept="image/*,.pdf"
                helperText={row.backAttachment ? row.backAttachment.name : 'No file chosen'}
                onChange={(e) => handleFileChange(row.id, 'back', e.target.files[0])}
                src={
                  row.backAttachment?.type === 'application/pdf'
                    ? 'pdf'
                    : row.backAttachment?.type === 'application/msword'
                    ? 'doc'
                    : row.backAttachment?.type ===
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ? 'docx'
                    : row.backAttachmentPreview
                }
              />
            </CCol>

            <CCol md={3} className="d-flex gap-2 mb-2">
              {index === attachmentRows.length - 1 && availableOptions.length > 0 && (
                <Button
                  title="Add"
                  onClick={handleAddRow}
                  className="btn-success"
                  icon={<CIcon icon={cilPlus} className="me-1" />}
                  disabled={!row.docType || !row.frontAttachment}
                />
              )}

              {attachmentRows.length > 1 && (
                <Button
                  title="Remove"
                  onClick={() => handleRemoveRow(row.id)}
                  className="btn-danger"
                  icon={<CIcon icon={cilTrash} className="me-1" />}
                />
              )}
            </CCol>
          </CRow>
        );
      })}
    </div>
  );
}

export default DAAttachment;
